/**
 * Aufschluesselung je Werk (D-08): Layout "plants" fuer die vier regionalen/Zementverwendungs-
 * Zeilen, Layout "plantMatrix" fuer "Meldung je Werk" bei Zementarten. Werte, Anteile und Summen
 * kommen fertig vom Server -- diese Komponente rechnet nichts nach, sie zeigt nur an. Der Aufruf
 * geht erst beim Oeffnen raus (Vorbild: `DrillDownModal.tsx`, `useEvaluationTable` fuer das
 * Fetch-Cancel-Skelett).
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Center,
  Loader,
  Modal,
  ScrollArea,
  Table,
  Text,
} from '@mantine/core';
import { evaluationTableApi } from '../../services/evaluationTableApi';
import { getApiErrorMessage } from '../../utils/errorHandling';
import { formatShare, formatTonnes } from './evaluationFormat';
import type { EvaluationType } from '../../types/evaluations';
import type {
  BreakdownResponse,
  PlantMatrixBreakdownResponse,
  PlantsBreakdownResponse,
} from '../../types/evaluationTable';

export interface EvaluationBreakdownModalProps {
  opened: boolean;
  onClose: () => void;
  evaluationType: EvaluationType;
  year: number;
  period: number;
  rowKey: string | null;
}

function complianceNote(response: PlantsBreakdownResponse): string | null {
  const { compliance } = response;
  if (!compliance) {
    return null;
  }
  if (compliance.ok) {
    return `${compliance.companies} Unternehmen mit ${compliance.plants} Werken, größter Anteil eines Unternehmens ${formatShare(compliance.maxShare)}.`;
  }
  return `Wird gesperrt: ${compliance.problems.join('; ')}.`;
}

function PlantsBreakdown({ response }: { response: PlantsBreakdownResponse }) {
  const note = complianceNote(response);
  return (
    <>
      {note &&
        (response.compliance?.ok ? (
          <Text size="sm" c="vdzGray.6" mb="sm">
            {note}
          </Text>
        ) : (
          <Alert color="red" mb="sm">
            {note}
          </Alert>
        ))}
      <ScrollArea h={response.plants.length > 20 ? 400 : undefined}>
        <Table striped="even" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Werk</Table.Th>
              <Table.Th>Unternehmen</Table.Th>
              <Table.Th>Menge (t)</Table.Th>
              <Table.Th>Anteil</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {response.plants.map((plant, index) => (
              <Table.Tr key={`${plant.plantName}-${index}`}>
                <Table.Td>{plant.plantName}</Table.Td>
                <Table.Td>{plant.companyName}</Table.Td>
                <Table.Td>{formatTonnes(plant.value)}</Table.Td>
                <Table.Td>{formatShare(plant.share)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td>Summe</Table.Td>
              <Table.Td>{response.plants.length} Werke</Table.Td>
              <Table.Td>{formatTonnes(response.total)}</Table.Td>
              <Table.Td>100,0 %</Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </ScrollArea>
    </>
  );
}

function PlantMatrixBreakdown({
  response,
}: {
  response: PlantMatrixBreakdownResponse;
}) {
  return (
    <>
      <Text size="sm" c="vdzGray.6" mb="sm">
        {response.longLabel}
      </Text>
      <ScrollArea h={response.plants.length > 20 ? 400 : undefined}>
        <Table striped="even" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Werk</Table.Th>
              {response.classes.map((cls) => (
                <Table.Th key={cls}>{cls}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {response.plants.map((plant, index) => (
              <Table.Tr key={`${plant.plantName}-${index}`}>
                <Table.Td>{plant.plantName}</Table.Td>
                {plant.values.map((value, i) => (
                  <Table.Td key={i}>
                    {value === null ? '' : formatTonnes(value)}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td>Summe</Table.Td>
              {response.classes.map((cls, i) => {
                const total = response.totals[i];
                return (
                  <Table.Td
                    key={cls}
                    c={total?.blocked ? 'red.9' : undefined}
                    bg={total?.blocked ? 'red.0' : undefined}
                  >
                    {total ? formatTonnes(total.value) : ''}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </ScrollArea>
    </>
  );
}

export function EvaluationBreakdownModal({
  opened,
  onClose,
  evaluationType,
  year,
  period,
  rowKey,
}: EvaluationBreakdownModalProps) {
  const [data, setData] = useState<BreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || !rowKey) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    evaluationTableApi
      .getBreakdown(evaluationType, year, period, rowKey)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            getApiErrorMessage(
              err,
              'Die Aufschlüsselung konnte nicht geladen werden.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [opened, evaluationType, year, period, rowKey]);

  const size = data?.layout === 'plantMatrix' ? 'xl' : 'lg';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={data?.title ?? ''}
      size={size}
      radius="md"
    >
      {loading && (
        <Center p="xl">
          <Loader size="md" />
        </Center>
      )}
      {!loading && error && (
        <Alert color="red" title="Nicht geladen">
          {error}
        </Alert>
      )}
      {!loading && !error && data?.importLocked && (
        <Text c="vdzGray.6">
          Für importierte Zeiträume liegen keine Werksdaten vor.
        </Text>
      )}
      {!loading &&
        !error &&
        data &&
        !data.importLocked &&
        data.layout === 'plants' && <PlantsBreakdown response={data} />}
      {!loading &&
        !error &&
        data &&
        !data.importLocked &&
        data.layout === 'plantMatrix' && (
          <PlantMatrixBreakdown response={data} />
        )}
    </Modal>
  );
}
